import { GeneralSettingsPatchDto, QueryFactory } from '@lmt/shared-types';
import {
  Button,
  ButtonColor,
} from '@lmt/unicorn-shared/app/components/common/ui-kit/buttons/Button';
import { SubmitButton } from '@lmt/unicorn-shared/app/components/common/ui-kit/buttons/SubmitButton';
import { useModal } from '@lmt/unicorn-shared/app/components/common/ui-kit/modal/useModal';
import { useLanguages } from '@lmt/unicorn-shared/app/constants/languages';
import { useClaims } from '@lmt/unicorn-shared/app/features/auth/operations';
import { useAdvancedForm } from '@lmt/unicorn-shared/app/helpers/form/advanced-form';
import { requiredRule } from '@lmt/unicorn-shared/app/helpers/form/react-hook-form-helper';
import { handleSubmitFormError } from '@lmt/unicorn-shared/app/helpers/form/useErrorHandler';
import { showGenerateDataDialog } from '@lmt/unicorn-shared/app/helpers/generate-data-dialog';
import { useScopedTranslation } from '@lmt/unicorn-shared/app/helpers/localization/useScopedTranslation';
import React, { FC, useCallback, useEffect, useMemo } from 'react';
import { Controller } from 'react-hook-form';
import { Field } from '../../../common/Field';
import { CheckBox } from '../../../common/ui-kit/CheckBox';
import { DropDownInput } from '../../../common/ui-kit/inputs/dropdown/DropDownInput';
import { Input } from '../../../common/ui-kit/inputs/Input';
import styles from '../SettingsBlock.module.scss';
import { TenantSettingsKeys } from '../types';
import { useRegisterUnsavedChanges } from '../useRegisterUnsavedChanges';
import {
  defaultGeneralSettingsData,
  dfnMergeFieldData,
  GeneralSettingsData,
} from './types';

export const GeneralSettings: FC = React.memo(function GeneralSettings() {
  const i18n = useScopedTranslation('tenant_settings.general_settings');
  const modals = useModal();
  const claims = useClaims();
  const languages = useLanguages();

  const { data: generalSettings, refetch: refetchGeneralSettings } =
    QueryFactory.GeneralSettingsQuery.useGetQuery();
  const mergeFieldQuery = QueryFactory.MergeFieldQuery.useGetQuery();
  const dfnMergeField = useMemo(() => {
    return (mergeFieldQuery.data ?? []).find(
      (x) =>
        x.code === dfnMergeFieldData.code &&
        x.prefix === dfnMergeFieldData.prefix,
    );
  }, [mergeFieldQuery.data]);

  const generateData = useCallback(async () => {
    const result = await showGenerateDataDialog(modals);
    if (result) {
      import('@lmt/unicorn-shared/app/helpers/generateTestData').then(
        (generateTestData) => {
          generateTestData.generateTestData(result);
        },
      );
    }
  }, []);

  const form = useAdvancedForm<GeneralSettingsData>(
    useCallback(
      async (data: GeneralSettingsData) => {
        try {
          const generalSettingsDto: GeneralSettingsPatchDto = {
            isPatientCreatingShortedFormEnabled:
              data.isPatientCreatingShortedFormEnabled,
            isVisitCreatingShortedFormEnabled:
              data.isVisitCreatingShortedFormEnabled,
            serverLocale: data.serverLocale,
          };
          await QueryFactory.GeneralSettingsClient.patch(generalSettingsDto);
          if (claims.permissions.documentTemplateManagement && dfnMergeField) {
            await QueryFactory.MergeFieldClient.patch(dfnMergeField.id, {
              expression: data.fileNameExpression,
            });
          }
          await mergeFieldQuery.refetch();
          await refetchGeneralSettings();
        } catch (error) {
          const errorDetails = handleSubmitFormError(error, setError);
          if (errorDetails.overallServerError) {
            await modals.showError({ text: errorDetails.overallServerError });
          }
        }
      },
      [dfnMergeField],
    ),
    useMemo(() => {
      return {
        ...defaultGeneralSettingsData,
        ...generalSettings,
        fileNameExpression: dfnMergeField?.expression ?? '',
      };
    }, [generalSettings, dfnMergeField]),
    {
      mode: 'onChange',
      shouldUnregister: false,
    },
  );
  const { formState, reset, register, setError, control } = form;

  const onReset = useCallback(() => {
    reset({
      ...defaultGeneralSettingsData,
      ...generalSettings,
      fileNameExpression: dfnMergeField?.expression ?? '',
    });
  }, [generalSettings, dfnMergeField]);

  useEffect(() => {
    onReset();
  }, [generalSettings, dfnMergeField]);

  const isUnsavedChanges = useRegisterUnsavedChanges(
    TenantSettingsKeys.General,
    form,
  );

  return (
    <form onSubmit={form.handleSubmitDefault}>
      <div
        className={styles.container}
        data-test-id="general-settings-container"
      >
        <div className={styles.header}>
          <div className={styles.title}>{i18n.t('title')}</div>
          <div className={styles.optionContainer}>
            {claims.permissions.generateTestData && (
              <Button
                title={i18n.t('generate_test_data.generate_button_title')}
                onClick={generateData}
                className={styles.option}
                color={ButtonColor.Danger}
              />
            )}
            <Button
              disabled={!isUnsavedChanges}
              title={i18n.t('reset_button_title')}
              className={styles.option}
              color={ButtonColor.Secondary}
              onClick={onReset}
            />
            <SubmitButton
              disabled={!isUnsavedChanges}
              title={i18n.t('submit_button_title')}
              submitting={formState.isSubmitting}
              className={styles.option}
            />
          </div>
        </div>
        <CheckBox
          title={i18n.t('patient_shorted_form_enabled')}
          className={styles.checkbox}
          {...register('isPatientCreatingShortedFormEnabled')}
        />
        <CheckBox
          title={i18n.t('visit_shorted_form_enabled')}
          className={styles.checkbox}
          {...register('isVisitCreatingShortedFormEnabled')}
        />
        {claims.permissions.documentTemplateManagement && (
          <Field
            title={i18n.t('file_name_expression')}
            className={styles['expression-field']}
            linkProps={{
              title: i18n.t('reset_to_default_dfn'),
              onClick: () =>
                form.setValue(
                  'fileNameExpression',
                  dfnMergeFieldData.expression,
                  {
                    shouldValidate: true,
                    shouldDirty: true,
                  },
                ),
            }}
          >
            <Input
              variant="normal"
              {...register('fileNameExpression', {
                ...requiredRule(),
                setValueAs: (value) => value.trim(),
              })}
              errorText={formState.errors.fileNameExpression?.message}
            />
          </Field>
        )}
        <Field
          title={i18n.t('server_locale')}
          className={styles['expression-field']}
          hint={i18n.t('server_locale_hint')}
        >
          <Controller
            name={'serverLocale'}
            control={control}
            render={({ field: { onChange, value } }) => (
              <DropDownInput
                useIdFunctionAsValue={true}
                getOptionLabel="label"
                idFunction="key"
                required
                options={languages}
                value={value}
                onValueChanged={(value) => onChange(value.key)}
              />
            )}
          ></Controller>
        </Field>
      </div>
    </form>
  );
});
